/**
 * FrameworkGeneratorPage - SPO框架生成器页面
 *
 * 功能：
 * 1. SPO模板选择（多种国际标准模板）
 * 2. 自动数据填充（从发行人提交信息）
 * 3. 框架文档预览（富文本编辑器）
 * 4. 发送给S&P分析师
 * 5. 生成历史记录
 * 6. 状态跟踪
 *
 * 权限控制：
 * - 运营商/管理员：生成、编辑、发送框架
 * - 发行人：仅查看框架
 * - 其他角色：仅查看
 */

// @ts-nocheck - TODO: Fix type definitions
import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  Button,
  Space,
  Alert,
  Descriptions,
  Modal,
  message,
  Spin,
  Timeline,
  Divider,
  Row,
  Col,
  Typography,
  Empty,
  Tag,
  Tabs,
  Input,
  Collapse,
  List,
  Steps,
} from 'antd';
import {
  FileTextOutlined,
  SendOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  FileDoneOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useGetSPOQuery,
  useGenerateFrameworkMutation,
  useGetIssuerStatusQuery,
} from '../../store/api/spoApi';
import { UnifiedStatusTag } from '../../components/unified';
import StandardPageLayout from '../../components/layout/StandardPageLayout';
import { useResolvedSpoId } from './hooks/useResolvedSpoId';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Step } = Steps;

interface FrameworkGeneratorPageProps {
  projectId?: string;
}

const FrameworkGeneratorPage: React.FC<FrameworkGeneratorPageProps> = ({
  projectId: propProjectId,
}) => {
  const { resolvedSpoId, isResolving, isResolveError } = useResolvedSpoId({
    projectId: propProjectId,
  });

  // ==================== State管理 ====================
  const [form] = Form.useForm();
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [frameworkContent, setFrameworkContent] = useState<string>('');
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('generator');

  // ==================== API Hooks ====================
  const {
    data: spoData,
    isLoading: spoLoading,
    error: spoError,
    refetch: refetchSPO,
  } = useGetSPOQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const {
    data: issuerStatusData,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useGetIssuerStatusQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const [generateFramework, { isLoading: generating }] =
    useGenerateFrameworkMutation();

  // ==================== 权限控制 ====================
  const userRole = 'operator'; // TODO: 从Redux Store获取实际用户角色
  const canGenerate = userRole === 'operator' || userRole === 'admin';
  const canEdit = userRole === 'operator' || userRole === 'admin';

  // ==================== 模板选项 ====================
  const TEMPLATE_OPTIONS = [
    {
      value: 'icma_green_bond',
      label: 'ICMA Green Bond Principles',
      description: '国际资本市场协会绿色债券原则标准框架',
      sections: ['项目选择', '募集资金管理', '项目评估与筛选', '报告'],
    },
    {
      value: 'cbi_climate_bond',
      label: 'CBI Climate Bonds Standard',
      description: '气候债券倡议组织标准框架',
      sections: ['气候效益', '项目认证', '持续监测', '报告披露'],
    },
    {
      value: 'eu_green_bond',
      label: 'EU Green Bond Standard',
      description: '欧盟绿色债券标准框架',
      sections: ['环境目标', '尽职调查', '信息披露', '外部审查'],
    },
    {
      value: 'sp_global_standard',
      label: 'S&P Global Green Evaluation',
      description: 'S&P全球绿色评估标准框架',
      sections: ['治理', '透明度', '管理', '环境影响'],
    },
  ];

  // ==================== 表单初始化 ====================
  useEffect(() => {
    const evaluation = spoData?.data?.evaluation;
    if (evaluation?.frameworkTemplateId) {
      setSelectedTemplate(evaluation.frameworkTemplateId);
      form.setFieldsValue({
        templateId: evaluation.frameworkTemplateId,
      });
    }
  }, [spoData, form]);

  // ==================== 生成框架 ====================
  const handleGenerateFramework = async () => {
    try {
      const values = await form.validateFields();

      if (!values.templateId) {
        message.error('请选择SPO框架模板！');
        return;
      }

      // 检查发行人是否已提交信息
      if (
        issuerStatusData?.data?.evaluation?.issuerSubmissionStatus !==
        'ISSUER_SUBMITTED'
      ) {
        message.warning('请先等待发行人提交项目信息！');
        return;
      }

      const result = await generateFramework({
        spoId: resolvedSpoId!,
        data: {
          templateId: values.templateId,
          customSections: values.customSections || [],
        },
      }).unwrap();

      if (result.success) {
        message.success('SPO框架生成成功！');
        setFrameworkContent(
          result.data?.evaluation?.frameworkDocumentUrl || ''
        );
        refetchSPO();
        refetchStatus();
      } else {
        message.error(result.error?.message || '框架生成失败');
      }
    } catch (error) {
      console.error('Generate framework error:', error);
      message.error('生成失败，请重试');
    }
  };

  // ==================== 发送框架给S&P ====================
  const handleSendFramework = async () => {
    try {
      // TODO: 调用发送框架API
      message.success('SPO框架已发送给S&P分析师！');
      setSendModalVisible(false);
      refetchSPO();
    } catch (error) {
      console.error('Send framework error:', error);
      message.error('发送失败，请重试');
    }
  };

  // ==================== 预览框架 ====================
  const handlePreview = () => {
    const evaluation = spoData?.data?.evaluation;
    if (evaluation?.frameworkDocumentUrl) {
      setFrameworkContent(evaluation.frameworkDocumentUrl);
      setPreviewVisible(true);
    } else {
      message.warning('请先生成框架！');
    }
  };

  // ==================== 渲染生成器 ====================
  const renderGenerator = () => {
    if (!canGenerate) {
      return (
        <Alert
          message="无生成权限"
          description="只有运营商和管理员可以生成SPO框架"
          type="warning"
          showIcon
        />
      );
    }

    return (
      <Card title="SPO框架生成" style={{ marginBottom: 24 }}>
        <Alert
          message="生成提示"
          description="选择合适的SPO框架模板，系统将自动从发行人提交的信息中填充数据"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical">
          <Form.Item
            label="选择SPO框架模板"
            name="templateId"
            rules={[{ required: true, message: '请选择框架模板' }]}
          >
            <Select
              placeholder="请选择合适的框架模板"
              onChange={setSelectedTemplate}
              size="large"
            >
              {TEMPLATE_OPTIONS.map(template => (
                <Option key={template.value} value={template.value}>
                  <div>
                    <strong>{template.label}</strong>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {template.description}
                    </Text>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* 模板详情 */}
          {selectedTemplate && (
            <Card
              size="small"
              title="模板结构预览"
              style={{ marginBottom: 16 }}
            >
              <Steps
                direction="vertical"
                current={-1}
                items={
                  TEMPLATE_OPTIONS.find(
                    t => t.value === selectedTemplate
                  )?.sections.map((section, index) => ({
                    title: `第 ${index + 1} 部分`,
                    description: section,
                    icon: <FileTextOutlined />,
                  })) || []
                }
              />
            </Card>
          )}

          <Form.Item label="自定义章节（可选）" name="customSections">
            <TextArea
              rows={4}
              placeholder="如需添加自定义章节，请在此输入（每行一个章节名称）"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                loading={generating}
                onClick={handleGenerateFramework}
              >
                生成SPO框架
              </Button>
              <Button
                size="large"
                icon={<ReloadOutlined />}
                onClick={() => {
                  form.resetFields();
                  setSelectedTemplate('');
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    );
  };

  // ==================== 渲染框架预览 ====================
  const renderPreview = () => {
    const evaluation = spoData?.data?.evaluation;

    if (!evaluation || !evaluation.frameworkGeneratedAt) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="尚未生成框架"
        />
      );
    }

    return (
      <Card title="框架文档预览">
        <Descriptions column={2} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="生成时间" span={2}>
            {dayjs(evaluation.frameworkGeneratedAt).format(
              'YYYY-MM-DD HH:mm:ss'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="使用模板">
            {
              TEMPLATE_OPTIONS.find(
                t => t.value === evaluation.frameworkTemplateId
              )?.label
            }
          </Descriptions.Item>
          <Descriptions.Item label="文档状态">
            <UnifiedStatusTag
              type="evaluationStatus"
              status={evaluation.issuerSubmissionStatus}
            />
          </Descriptions.Item>
        </Descriptions>

        {/* 框架内容预览 */}
        <Card
          size="small"
          title="文档内容"
          extra={
            <Space>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={handlePreview}
              >
                全屏预览
              </Button>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() =>
                  window.open(evaluation.frameworkDocumentUrl, '_blank')
                }
              >
                下载
              </Button>
            </Space>
          }
        >
          <div
            style={{
              height: 400,
              overflow: 'auto',
              border: '1px solid #f0f0f0',
              padding: 16,
              backgroundColor: '#fafafa',
            }}
          >
            <Paragraph>
              {evaluation.frameworkDocumentUrl ? (
                <iframe
                  src={evaluation.frameworkDocumentUrl}
                  style={{ width: '100%', height: 360, border: 'none' }}
                  title="Framework Preview"
                />
              ) : (
                <Text type="secondary">文档预览不可用，请下载后查看</Text>
              )}
            </Paragraph>
          </div>
        </Card>

        {/* 操作按钮 */}
        {canGenerate && (
          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => setSendModalVisible(true)}
            >
              发送给S&P分析师
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleGenerateFramework}>
              重新生成
            </Button>
          </Space>
        )}
      </Card>
    );
  };

  // ==================== 渲染生成历史 ====================
  const renderHistory = () => {
    const timeline = spoData?.data?.timeline || [];
    const frameworkEvents = timeline.filter(
      event =>
        event.type === 'framework_generated' || event.type === 'framework_sent'
    );

    if (frameworkEvents.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无生成历史"
        />
      );
    }

    return (
      <Card title="生成历史记录">
        <Timeline>
          {frameworkEvents.map((event, index) => (
            <Timeline.Item
              key={index}
              color={event.type === 'framework_sent' ? 'green' : 'blue'}
              dot={
                event.type === 'framework_sent' ? (
                  <SendOutlined />
                ) : (
                  <FileDoneOutlined />
                )
              }
            >
              <div>
                <Text strong>
                  {event.type === 'framework_sent'
                    ? '框架已发送'
                    : '框架已生成'}
                </Text>
                <br />
                <Text type="secondary">
                  {dayjs(event.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                </Text>
                <br />
                <Text type="secondary">操作人：{event.actor || '系统'}</Text>
                {event.data && (
                  <>
                    <br />
                    <Text type="secondary">
                      备注：{JSON.stringify(event.data)}
                    </Text>
                  </>
                )}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </Card>
    );
  };

  // ==================== Loading/Error处理 ====================
  if (isResolving || spoLoading || statusLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>加载SPO框架信息中...</div>
      </div>
    );
  }

  if (spoError || isResolveError || !resolvedSpoId) {
    return (
      <Alert
        message="加载失败"
        description="无法加载SPO流程信息，请刷新页面重试"
        type="error"
        showIcon
      />
    );
  }

  // ==================== 主渲染 ====================
  return (
    <StandardPageLayout
      config={{
        title: 'SPO框架生成器',
        description: '选择模板，自动生成SPO评估框架文档',
      }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <ThunderboltOutlined />
              框架生成
            </span>
          }
          key="generator"
        >
          {renderGenerator()}
        </TabPane>

        <TabPane
          tab={
            <span>
              <EyeOutlined />
              框架预览
            </span>
          }
          key="preview"
        >
          {renderPreview()}
        </TabPane>

        <TabPane
          tab={
            <span>
              <HistoryOutlined />
              生成历史
            </span>
          }
          key="history"
        >
          {renderHistory()}
        </TabPane>
      </Tabs>

      {/* 框架预览Modal */}
      <Modal
        open={previewVisible}
        title="SPO框架全屏预览"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="90%"
        style={{ top: 20 }}
      >
        <div style={{ height: '70vh', overflow: 'auto' }}>
          {frameworkContent ? (
            <iframe
              src={frameworkContent}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Framework Full Preview"
            />
          ) : (
            <Empty description="无法加载预览" />
          )}
        </div>
      </Modal>

      {/* 发送确认Modal */}
      <Modal
        open={sendModalVisible}
        title="发送SPO框架给S&P"
        onOk={handleSendFramework}
        onCancel={() => setSendModalVisible(false)}
        okText="确认发送"
        cancelText="取消"
      >
        <Alert
          message="确认发送"
          description="确认将SPO框架文档发送给S&P分析师？发送后将触发邮件通知。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Descriptions column={1} bordered>
          <Descriptions.Item label="接收人">
            {spoData?.data?.engagementLetter?.spAnalystEmail || 'S&P分析师'}
          </Descriptions.Item>
          <Descriptions.Item label="文档名称">
            SPO Framework - {spoData?.data?.projectId}
          </Descriptions.Item>
          <Descriptions.Item label="发送时间">
            {dayjs().format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </StandardPageLayout>
  );
};

export default FrameworkGeneratorPage;
